import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import * as React from "react";

import { RobotoMarkdown } from "@/shared/components/RobotoMarkdown";
import { useUpdateCollection } from "@/shared/domain/collections/hooks/useUpdateCollection";
import { Button } from "@/shared/foundation/primitives";

import styles from "./EditableCollectionDescription.module.css";

interface EditableCollectionDescriptionProps {
  description: string;
  collectionId: string;
  orgId: string;
}

export const EditableCollectionDescription: React.FC<
  EditableCollectionDescriptionProps
> = ({ description, collectionId, orgId }) => {
  const [editable, setEditable] = React.useState(false);
  const [current, setCurrent] = React.useState(description);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [dialogEditing, setDialogEditing] = React.useState(false);
  const [dialogValue, setDialogValue] = React.useState("");
  const [isOverflowing, setIsOverflowing] = React.useState(false);
  const [isAtBottom, setIsAtBottom] = React.useState(false);
  const savingRef = React.useRef(false);
  const cancelledRef = React.useRef(false);
  const descRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    setCurrent(description);
  }, [description, collectionId]);

  React.useEffect(() => {
    const el = descRef.current;
    if (!el) {
      return;
    }
    const checkOverflow = () => {
      setIsOverflowing(el.scrollHeight > el.clientHeight);
      setIsAtBottom(el.scrollTop + el.clientHeight >= el.scrollHeight - 1);
    };
    checkOverflow();
    const observer = new ResizeObserver(checkOverflow);
    observer.observe(el);
    el.addEventListener("scroll", checkOverflow);
    return () => {
      observer.disconnect();
      el.removeEventListener("scroll", checkOverflow);
    };
  }, [current]);

  const { mutateAsync: updateCollection } = useUpdateCollection();

  const persist = async (value: string): Promise<boolean> => {
    if (savingRef.current) {
      return false;
    }
    savingRef.current = true;
    const trimmed = value.trim();
    if (trimmed === current) {
      savingRef.current = false;
      return true;
    }
    try {
      await updateCollection({
        collectionId,
        resourceOwnerId: orgId,
        updates: { description: trimmed || undefined },
      });
      setCurrent(trimmed);
      savingRef.current = false;
      return true;
    } catch {
      savingRef.current = false;
      return false;
    }
  };

  const save = async (value: string) => {
    if (cancelledRef.current) {
      return;
    }
    const ok = await persist(value);
    if (ok) {
      setEditable(false);
    }
  };

  const saveFromDialog = async () => {
    const ok = await persist(dialogValue);
    if (ok) {
      setDialogEditing(false);
      setDialogOpen(false);
    }
  };

  const openDialogEdit = () => {
    setDialogValue(current);
    setDialogEditing(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setDialogEditing(false);
  };

  return (
    <>
      <div style={{ display: editable ? "none" : undefined }}>
        <div className={styles.descriptionWrapper}>
          <div
            ref={descRef}
            className={styles.description}
            onClick={() => {
              cancelledRef.current = false;
              setEditable(true);
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                cancelledRef.current = false;
                setEditable(true);
              }
            }}
          >
            {current ? (
              <RobotoMarkdown content={current} />
            ) : (
              <i>No description</i>
            )}
          </div>
          {isOverflowing && !isAtBottom && (
            <div className={styles.fade} aria-hidden />
          )}
        </div>

        {isOverflowing && (
          <button
            className={styles.showMore}
            onClick={() => setDialogOpen(true)}
          >
            Expand
          </button>
        )}
      </div>

      {editable && (
        <TextField
          autoFocus
          size="small"
          fullWidth
          multiline
          minRows={2}
          maxRows={10}
          defaultValue={current}
          placeholder="No description"
          onBlur={(e) => void save(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              cancelledRef.current = true;
              setEditable(false);
            }
            if (e.key === "Enter" && e.shiftKey) {
              e.preventDefault();
              void save((e.target as HTMLTextAreaElement).value);
            }
          }}
          className={styles.textField}
          inputProps={{
            style: {
              fontSize: "var(--font-size-sm)",
              color: "var(--text-secondary)",
            },
          }}
        />
      )}

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle>Description</DialogTitle>
        <DialogContent>
          {dialogEditing ? (
            <TextField
              autoFocus
              fullWidth
              multiline
              minRows={6}
              value={dialogValue}
              onChange={(e) => setDialogValue(e.target.value)}
              placeholder="No description"
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  setDialogEditing(false);
                }
                if (e.key === "Enter" && e.shiftKey) {
                  e.preventDefault();
                  void saveFromDialog();
                }
              }}
              inputProps={{
                style: { fontSize: "var(--font-size-sm)" },
              }}
            />
          ) : (
            <div
              className={styles.dialogDescription}
              onClick={openDialogEdit}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  openDialogEdit();
                }
              }}
            >
              <RobotoMarkdown content={current || "No description"} />
            </div>
          )}
        </DialogContent>
        <DialogActions>
          {dialogEditing ? (
            <>
              <Button onClick={() => setDialogEditing(false)}>Cancel</Button>
              <Button variant="contained" onClick={() => void saveFromDialog()}>
                Save
              </Button>
            </>
          ) : (
            <Button onClick={closeDialog}>Close</Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};
